import React, { useState, useEffect } from "react";
import { Search, Loader2, CheckCircle2, X, Send } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../../../chat/api"; // Adjust path if needed
import Checkbox from "../../../../../Checkbox"; // Adjust path if needed

const normalizeMember = (member) => {
  const displayName = member.full_name || member.email || "Unknown";
  const nameParts = displayName.trim().split(/\s+/);
  let initials = "";
  if (nameParts.length > 0) {
    const firstInitial = nameParts[0][0] || "";
    const lastInitial =
      nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : "";
    initials = (firstInitial + lastInitial).toUpperCase();
  } else {
    initials = "??";
  }
  return {
    id: member.user_id,
    name: displayName,
    role: member.permission_role || "Viewer",
    initials: initials,
    color: "bg-[#8B5CF6]",
  };
};

const TeamAssignmentView = ({
  projectId,
  orgId,
  question,
  onAssign,
  onRefresh,
  documentPermission,
}) => {
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [assignStatus, setAssignStatus] = useState("idle");
  const [commentText, setCommentText] = useState("");
  const [unassigningUserId, setUnassigningUserId] = useState(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  useEffect(() => {
    const fetchMembers = async () => {
      setIsLoadingMembers(true);
      try {
        const response = await api.get(
          `/api/v1/rfp/folders/${projectId}/section-view/members/list/?organization_id=${orgId}&include_org_members=true`
        );
        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
          ? response.data.results
          : [];
        setMembers(list.map(normalizeMember));
      } catch (error) {
        console.error("Error fetching members:", error);
      } finally {
        setIsLoadingMembers(false);
      }
    };
    if (projectId && orgId) fetchMembers();
  }, [projectId, orgId]);

  const filteredTeammates = members.filter((member) =>
    member.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAssignSubmit = async () => {
    if (selectedIds.length === 0) return;
    setAssignStatus("assigning");
    const success = await onAssign(selectedIds);
    if (success) {
      setAssignStatus("success");
      setSelectedIds([]);
      setTimeout(() => setAssignStatus("idle"), 2000);
    } else {
      setAssignStatus("idle");
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleUnassignMember = async (userId) => {
    if (!userId) return toast.error("Invalid member.");
    if (!question?.uid) return toast.error("Invalid question.");
    if (unassigningUserId) return;

    setUnassigningUserId(userId);
    try {
      const response = await api.post(
        `/api/v1/rfp/folders/${projectId}/section-view/unassign-member/?questionnaire=true`,
        {
          org_id: parseInt(orgId),
          question_id: question.uid,
          user_id: userId,
        }
      );

      if (response.status === 200 || response.status === 201) {
        if (typeof onRefresh === "function") {
          await onRefresh();
        }
      } else {
        throw new Error("Failed to unassign");
      }
    } catch (error) {
      console.error("Unassign error:", error);
      toast.error("Failed to unassign teammate.");
    } finally {
      setUnassigningUserId(null);
    }
  };

  const handleCommentSubmit = async () => {
    const trimmed = (commentText || "").trim();
    if (!trimmed) return;
    if (!question?.uid) return toast.error("Invalid question.");
    if (isSubmittingComment) return;

    setIsSubmittingComment(true);
    try {
      const response = await api.post(
        `/api/v1/rfp/folders/${projectId}/section-view/add-comment/?questionnaire=true`,
        {
          org_id: parseInt(orgId),
          question_id: question.uid,
          comment: trimmed,
        }
      );

      if (response.status === 200 || response.status === 201) {
        setCommentText("");
        if (typeof onRefresh === "function") {
          await onRefresh();
        }
      } else {
        throw new Error("Failed to add comment");
      }
    } catch (error) {
      console.error("Add comment error:", error);
      toast.error(error.response?.data?.message || "Failed to add comment.");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const comments = Array.isArray(question?.comments) ? question.comments : [];

  const formatRole = (role) => {
    if (!role) return "Viewer";
    const s = String(role);
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getAuthorLabel = (authorId) => {
    const found = members.find((m) => Number(m.id) === Number(authorId));
    const name = found?.name || "Himanshu"; 
    const role = formatRole(found?.role || "Owner");
    return { name, role };
  };

  // ----------------------------------------------------
  // EDITOR / REVIEWER VIEW CONDITIONAL RENDER
  // ----------------------------------------------------
  if (documentPermission === "editor" || documentPermission === "reviewer") {
    return (
      <div className="h-[calc(100vh-500px)] bg-[#FAFAFA] p-3">
        <div className="h-full bg-white border border-gray-200 rounded-xl py-3 px-4 flex flex-col overflow-hidden">
          <h3 className="text-[14px] font-semibold text-[#18101C] mb-3 shrink-0">
            Assigned Question
          </h3>

          {/* Comments Section */}
          <div className="flex flex-col flex-1 overflow-hidden min-h-0">
            <p className="text-[13px] text-gray-500 mb-2 shrink-0 border-b border-gray-100 pb-2">
              Comments ({comments.length})
            </p>

            <div className="flex-1 overflow-y-auto custom-scrollbar mb-3 pr-2 pt-1 space-y-4">
              {comments.length > 0 ? (
                comments.map((c, idx) => {
                  const text = c?.text ?? "";
                  const authorId = c?.author_id;
                  const { name, role } = getAuthorLabel(authorId);

                  return (
                    <div key={`${authorId || "na"}-${idx}`}>
                      <p className="text-[13px] font-semibold text-[#18101C]">
                        {name} ({role}) :
                      </p>
                      <p className="text-[13px] text-[#52525B] leading-relaxed mt-0.5">
                        {text}
                      </p>
                    </div>
                  );
                })
              ) : (
                <div className="text-[13px] text-gray-400 italic">No comments yet.</div>
              )}
            </div>

            {/* Add a Comment Input */}
            <div className="relative shrink-0 mt-auto">
              <input
                type="text"
                placeholder="Add a comment"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCommentSubmit();
                  }
                }}
                className="w-full h-10 pl-4 pr-10 text-[13px] font-normal bg-white border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C95EBE]"
              />
              <button
                onClick={handleCommentSubmit}
                disabled={isSubmittingComment}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#C95EBE] transition-colors"
              >
                {isSubmittingComment ? (
                  <Loader2 size={16} className="animate-spin text-[#C95EBE]" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
          </div>
        </div>

        <style jsx>{`
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background-color: #e4e4e7;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background-color: #d4d4d8;
          }
        `}</style>
      </div>
    );
  }

  // ----------------------------------------------------
  // ADMIN/OWNER VIEW (Original Split Layout)
  // ----------------------------------------------------
  return (
    <div className="h-[calc(100vh-500px)] bg-[#FAFAFA] p-3">
      <div className="h-full flex gap-1">
        {/* Left Section */}
        <div className="w-[38%] bg-white border border-gray-200 rounded-xl py-2 px-4 flex flex-col gap-3 overflow-hidden">
          <h4 className="text-[14px] font-semibold text-[#18101C]">
            Assign to a Teammate
          </h4>

          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
            <input
              type="text"
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full h-10 pl-8 pr-4 text-[14px] leading-[20px] hover:text-[14px] focus:text-[14px] bg-white border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE] shadow-sm transition-[border-color,box-shadow]"
              style={{ fontSize: "14px", lineHeight: "20px" }}
            />
          </div>

          <div className="flex-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm overflow-hidden">
            <div className="h-full max-h-full overflow-y-auto custom-scrollbar pr-1">
              {isLoadingMembers ? (
                <div className="py-10 flex justify-center items-center text-gray-400">
                  <Loader2 size={22} className="animate-spin text-[#C4A5E8]" />
                </div>
              ) : (
                <>
                  {filteredTeammates.map((member) => (
                    <div
                      key={member.id}
                      onClick={() => toggleSelection(member.id)}
                      className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer group transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full ${member.color} text-white flex items-center justify-center text-[13px] font-medium shrink-0`}
                        >
                          {member.initials}
                        </div>
                        <div>
                          <p className="text-[14px] font-semibold text-[#18101C] leading-tight">
                            {member.name}
                          </p>
                          <p className="text-[13px] text-gray-500 leading-tight mt-1">
                            {member.role}
                          </p>
                        </div>
                      </div>

                      <Checkbox
                        checked={selectedIds.includes(member.id)}
                        onChange={() => {}}
                        id={`member-inline-${member.id}`}
                        name={`member-inline-${member.id}`}
                        size="md"
                        showLabel={false}
                        selectionMode="multiple"
                      />
                    </div>
                  ))}

                  {!isLoadingMembers && filteredTeammates.length === 0 && (
                    <div className="py-10 text-center text-gray-400 text-sm">
                      No teammates found
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={handleAssignSubmit}
              disabled={selectedIds.length === 0 || assignStatus !== "idle"}
              className={`
                w-[120px] py-2 rounded-md text-[14px] font-semibold transition-all shadow-sm flex items-center justify-center gap-2
                ${
                  assignStatus === "success"
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-[#8B55DD] text-white disabled:opacity-70 disabled:cursor-not-allowed"
                }
              `}
            >
              {assignStatus === "assigning" ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Assigning...
                </>
              ) : assignStatus === "success" ? (
                <>
                  <CheckCircle2 size={16} /> Assigned
                </>
              ) : (
                "Assign"
              )}
            </button>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex-1 bg-white border border-gray-200 rounded-xl py-2 px-4 flex flex-col gap-3 overflow-hidden">
          <div className="flex flex-col gap-1">
            <h4 className="text-[14px] mb-1 font-semibold text-[#18101C]">
              Assigned Teammates
            </h4>
            <div className="flex gap-2 overflow-x-auto pb-2 custom-scrollbar">
              {question.assignedMembers && question.assignedMembers.length > 0 ? (
                question.assignedMembers.map((m, idx) => {
                  const displayName =
                    m.full_name || m.email || m.first_name || "Member";
                  const truncatedName =
                    displayName.length > 15
                      ? displayName.substring(0, 15) + "..."
                      : displayName;
                  const memberId = m.user_id || m.id;

                  return (
                    <div
                      key={idx}
                      className="flex items-center gap-2 bg-[#F3E8FF] px-3 py-1.5 rounded-full border border-[#E9D5FF] shrink-0"
                      title={displayName}
                    >
                      <span className="text-[13px] font-medium text-[#6B21A8]">
                        {truncatedName}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnassignMember(memberId);
                        }}
                        disabled={unassigningUserId === memberId}
                        className="text-[#6B21A8] hover:text-[#4C1D95] rounded-full p-0.5"
                      >
                        {unassigningUserId === memberId ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <X size={12} />
                        )}
                      </button>
                    </div>
                  );
                })
              ) : (
                <span className="text-[13px] text-gray-400 italic">
                  No one assigned yet.
                </span>
              )}
            </div>
          </div>

          <div className="h-px bg-gray-100 w-full"></div>

          <div className="flex-1 flex flex-col gap-1 overflow-hidden">
            <h4 className="text-[14px] font-medium text-gray-500">
              Comments ({comments.length})
            </h4>
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 pt-1">
              {comments.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {comments.map((c, idx) => {
                    const text = c?.text ?? "";
                    const authorId = c?.author_id;
                    const { name, role } = getAuthorLabel(authorId);

                    return (
                      <div key={`${authorId || "na"}-${c?.timestamp || idx}`}>
                        <p className="text-[14px] font-semibold text-[#18101C]">
                          {name} ({role}) :
                        </p>
                        <p className="text-[14px] text-[#52525B] leading-relaxed mt-0.5">
                          {text}
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-6 text-center text-gray-300 text-xs italic">
                  No comments yet. Start the discussion!
                </div>
              )}
            </div>

            <div className="relative mt-auto">
              <input
                type="text"
                placeholder="Add a comment..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCommentSubmit();
                  }
                }}
                className="w-full h-10 pl-4 pr-10 text-[13px] font-normal bg-white border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C95EBE]"
                style={{ fontSize: "13px" }}
              />
              <button
                onClick={handleCommentSubmit}
                disabled={isSubmittingComment}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#C95EBE]"
              >
                {isSubmittingComment ? (
                  <Loader2 size={16} className="animate-spin text-[#C95EBE]" />
                ) : (
                  <Send size={16} />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #e4e4e7;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #d4d4d8;
        }
      `}</style>
    </div>
  );
};

export default TeamAssignmentView;
